import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { OperatorTypes } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import ThresholdRangeTypes from '@avtechsoftware/ra-javascript-utilities/src/types/ThresholdRangeTypes';
import { getNextNumericalName } from '@avtechsoftware/ra-javascript-utilities/src/utilities/validation/ObjectListUtils';
import ReadingType, { findReadingTypeById } from '@avtechsoftware/ra-javascript-utilities/src/types/ReadingType';
import createInstanceCopy from '@avtechsoftware/ra-javascript-utilities/src/utilities/ObjectUtil';

import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import AltErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/AltErrorWrapper/AltErrorWrapper';

import {
  createThresholdRange,
  fetchThresholds,
  selectAllThresholdRanges,
  selectThresholdCrudStatus,
  selectThresholdRangeErrors,
  setThresholdRangeCrudStatus,
} from '../../../../redux/slices/thresholdRangeSlice/thresholdRangeSlice';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';
import { CrudStatus } from '../../../../utility/types/CrudStatus';
import RaaThresholdRange from '../../../../classes/RaaThresholdRange';
import {
  AlertFormSteps,
  selectThresholdData,
  updateStepData,
} from '../../../../redux/slices/alertFormSlice/alertFormSlice';

const NewSharedThresholdModal = ({
  onClose,
  initialReadingType,
  initialOperatorId,
  initialValue,
}) => {
  const dispatch = useDispatch();
  const sessionInformation = useSelector(selectSessionInformation);
  const thresholdList = useSelector(selectAllThresholdRanges);
  const errorList = useSelector(selectThresholdRangeErrors);
  const crudStatus = useSelector(selectThresholdCrudStatus);
  const selectedThresholdRangeData = useSelector(selectThresholdData);

  const { activeSubscriptionId } = sessionInformation;

  const [thresholdRange, setThresholdRange] = useState(() => {
    const tr = new RaaThresholdRange(
      null,
      ThresholdRangeTypes.CUSTOM,
      null,
      parseInt(initialOperatorId, 10),
      activeSubscriptionId,
      getNextNumericalName(thresholdList, 'New Threshold Range', 'name'),
      initialValue,
      '',
      0,
      -1,
      null,
      null,
      parseInt(initialReadingType, 10),
      0,
      0,
    );
    return createInstanceCopy(tr);
  });

  const [units, setUnits] = useState('');

  useEffect(() => {
    if (thresholdRange && thresholdRange.readingType != null && thresholdRange.readingType !== -1) {
      const rt = findReadingTypeById(thresholdRange.readingType);
      if (rt) {
        setUnits(rt.getUnits(sessionInformation));
      }
    } else {
      setUnits('');
    }
  }, [thresholdRange.readingType,
    sessionInformation.temperatureScale,
    sessionInformation.airSpeedScale]);

  useEffect(() => {
    if (crudStatus === CrudStatus.SUCCESS) {
      dispatch(setThresholdRangeCrudStatus(CrudStatus.IDLE));
      dispatch(fetchThresholds());
      if (typeof onClose === 'function') onClose();
    } else if (crudStatus === CrudStatus.ERROR) {
      dispatch(setThresholdRangeCrudStatus(CrudStatus.IDLE));
    }
  }, [crudStatus, dispatch, onClose]);

  const onChange = (field, value) => {
    setThresholdRange((prev) => {
      const next = Object.assign(Object.create(Object.getPrototypeOf(prev)), prev);
      next[field] = value;
      return next;
    });
  };

  const handleSave = () => {
    const updated = Object.assign(Object.create(
      Object.getPrototypeOf(thresholdRange),
    ), thresholdRange);
    const rt = findReadingTypeById(thresholdRange.readingType);

    if (rt) {
      updated.value = thresholdRange.value === '' ? null : thresholdRange.value;
      updated.channelTypeId = rt.channelTypeId;
    } else {
      updated.value = null;
      updated.channelTypeId = null;
    }
    try {
      dispatch(createThresholdRange(updated)).unwrap()
        .then((newThresholdRange) => {
          const thresholdId = newThresholdRange && newThresholdRange.id
            ? newThresholdRange.id
            : -1;
          dispatch(updateStepData({
            step: AlertFormSteps.DEFINE_THRESHOLD,
            data: { ...selectedThresholdRangeData, thresholdId, useSharedThreshold: true },
          }));
        });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('async thunk error when creating new threshold range: ', e);
      dispatch(updateStepData({
        step: AlertFormSteps.DEFINE_THRESHOLD,
        data: { ...selectedThresholdRangeData, thresholdId: -1 },
      }));
    }
  };

  const handleClose = () => {
    dispatch(setThresholdRangeCrudStatus(CrudStatus.IDLE));
    if (typeof onClose === 'function') onClose();
  };

  const operatorOptions = [
    { id: OperatorTypes.GREATER_THAN, label: 'is above' },
    { id: OperatorTypes.LESS_THAN, label: 'is below' },
  ];

  return (
    <div className="modal d-block" role="dialog" aria-modal="true" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">New Threshold Range</h5>
            <button type="button" className="close" aria-label="Close" onClick={handleClose}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className="modal-body">
            <FormErrorHandler fieldList={['name', 'value', 'readingType']} errorList={errorList}>
              <div className="form-group text-left">
                <AltErrorWrapper fieldId="name">
                  <input
                    className="form-control w-50"
                    placeholder="Name"
                    value={thresholdRange.name || ''}
                    onChange={(e) => onChange('name', e.target.value)}
                  />
                </AltErrorWrapper>
              </div>

              <div className="form-group text-left">
                <AltErrorWrapper fieldId="readingType">
                  <select
                    className="form-control w-auto"
                    value={thresholdRange.readingType}
                    onChange={(e) => onChange('readingType', parseInt(e.target.value, 10))}
                    disabled
                  >
                    <option value={-1} disabled> - Choose Reading Type - </option>
                    {Object.values(ReadingType).filter((rt) => rt.id > 0).map((rt) => (
                      <option key={rt.id} value={rt.id}>{rt.displayName}</option>
                    ))}
                  </select>
                </AltErrorWrapper>
              </div>

              <div className="form-group text-left">
                <AltErrorWrapper fieldId="operatorId">
                  <select
                    className="form-control w-50"
                    value={thresholdRange.operatorId}
                    onChange={(e) => onChange('operatorId', parseInt(e.target.value, 10))}
                  >
                    <option value={-1} disabled> - Choose Operator - </option>
                    {operatorOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>
                </AltErrorWrapper>
              </div>

              <div className="form-group text-left">
                <AltErrorWrapper fieldId="value">
                  <div className="form-row align-items-center">
                    <div className="col-auto">
                      <input
                        className="form-control"
                        style={{ width: '150px' }}
                        placeholder="Enter Value"
                        type="number"
                        value={thresholdRange.value != null ? thresholdRange.value : ''}
                        onChange={(e) => onChange('value', e.target.value)}
                      />
                    </div>
                    {units ? <div className="col-auto">{units}</div> : null}
                  </div>
                </AltErrorWrapper>
              </div>
            </FormErrorHandler>

            {errorList && errorList.length > 0 && (
              <div className="alert alert-danger mt-2" role="alert">
                {errorList.map((err, idx) => (
                  <div key={idx}>
                    {err.field}
                    :
                    {' '}
                    {err.message}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

NewSharedThresholdModal.defaultProps = {
  initialReadingType: -1,
  initialOperatorId: -1,
  initialValue: '',
};

NewSharedThresholdModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  initialReadingType: PropTypes.number,
  initialOperatorId: PropTypes.number,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default NewSharedThresholdModal;
